import axios, { CancelToken } from 'axios';
import { message } from 'antd';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

import { routes } from 'config/routes';
import { MetaState } from 'entities/Meta';
import { AuthorizationState } from 'entities/User';
import { rootStore } from 'stores/RootStore';
import { ILocalStore } from 'stores/interfaces/ILocalStore';

import { callApi } from './api';
import { ApiCallArgs, ApiResponse } from './types';

type PrivateFields = '_meta';

export default class ApiRequest implements ILocalStore {
  constructor(params: ApiCallArgs) {
    makeObservable<ApiRequest, PrivateFields>(this, {
      _meta: observable.ref,
      meta: computed,
      loading: computed,
      error: computed,
      success: computed,
      call: action.bound,
    });
    this._params = params;
  }

  private readonly _params: ApiCallArgs;

  private _cancelToken: (() => void) | null = null;
  private _requestIndicator: CancelToken | null = null;
  private _meta: MetaState = MetaState.initial;

  get meta(): MetaState {
    return this._meta;
  }

  get loading(): boolean {
    return this._meta === MetaState.loading;
  }

  get success(): boolean {
    return this._meta === MetaState.success;
  }

  get error(): boolean {
    return this._meta === MetaState.failed;
  }

  async call<
    Response = any,
    Request extends Record<string, unknown> | FormData = Record<string, unknown>
  >(
    params?: Omit<ApiCallArgs<Request>, 'endpoint'> & { endpoint?: string }
  ): Promise<ApiResponse<Response>> {
    const cancelToken = this._createRequestCanceller();

    runInAction(() => {
      this._meta = MetaState.loading;
    });

    const response = await callApi<Response>({
      endpoint: this._params.endpoint,
      methodType: this._params.methodType,
      config: {
        ...this._params.config,
        ...params,
        cancelToken,
      },
    });

    runInAction(() => {
      if (response.isError) {
        this._meta = MetaState.failed;
      } else {
        this._meta = MetaState.success;
      }
    });

    if (response.status === 401) {
      // Проверяем, является ли это ошибкой истекшей сессии
      if (response.code === 'session_expired' || response.message?.includes('сессия истекла')) {
        message.warning('Сессия истекла. Пожалуйста, войдите снова.');
      }
      rootStore.userStore.setAuthorized(AuthorizationState.unauthorized);
    }

    if (response.status === 403) {
      rootStore.routerStore?.navigate(routes.error['forbidden'].createPath());
    }

    return {
      ...response,
      data: response.data?.data || null,
    };
  }

  cancel(): void {
    this._cancelToken?.();
    this._cancelToken = null;
    this._requestIndicator = null;
    this._meta = MetaState.initial;
  }

  private _createRequestCanceller(): CancelToken {
    this._cancelToken?.();

    // eslint-disable-next-line import/no-named-as-default-member
    this._requestIndicator = new axios.CancelToken((canceller) => {
      this._cancelToken = canceller;
    });

    return this._requestIndicator;
  }

  destroy(): void {
    this.cancel();
  }
}
